import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { WISHLIST_ENABLED, HEADER_ACTIONS_HIDDEN } from '../../config/features';
import {
  AppBar, Toolbar, Box, Container, Typography, IconButton,
  Button, Popover, Menu, MenuItem, Drawer, List, ListItemButton,
  ListItemText, Collapse, Badge, Divider, Avatar,
} from '@mui/material';
import {
  KeyboardArrowDown,
  Menu as MenuIcon, ExpandMore, ExpandLess,
  PersonOutline, ReceiptLongOutlined, FavoriteBorder, LogoutOutlined, Close as CloseIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuth } from '../../auth/useAuth';
import { NAV_PRODUCTS } from '../../data/navData';
import { getMenuItems } from '../../api/menus';
import { CONTAINER_SX } from '../../style/containers';
import { withTrailingSlash } from '../../utils/url';

const CartDrawer = lazy(() => import('./CartDrawer'));
const SearchModal = lazy(() => import('./SearchModal'));

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const BRAND_BLUE = '#005DAA'; // Dynalog logo blue — active/hover/buttons
const NAV_COLOR = '#333333'; // Default nav text
const BORDER_COLOR = '#e9edf2';

const STORAGE_BASE = (process.env.REACT_APP_STORAGE_BASE || '').replace(/\/$/, '');

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return `${STORAGE_BASE}${normalized}`;
};

// ─── Menu API helpers ─────────────────────────────────────────────────────────

const nameToSlug = (name) => (name || '').toLowerCase().replace(/\s*&\s*/g, '-and-').replace(/\s+/g, '-').replace(/-{2,}/g, '-').replace(/^-|-$/g, '');

const buildItemUrl = (item) => {
  if (item.url && item.url !== '#') {
    return withTrailingSlash(item.url.startsWith('/') ? item.url : `/${item.url}`);
  }
  if (item.id) {
    const slugOrId = item.slug || item.category_slug || item.id;
    return withTrailingSlash(`/products/${slugOrId}`);
  }
  const name = item.name || item.label || '';
  return name ? withTrailingSlash(`/${nameToSlug(name)}`) : '#';
};

const transformMenuData = (menuItems) => {
  const productsMenu = menuItems.find((m) => m.name === 'Products');
  const industriesMenu = menuItems.find((m) => m.name === 'Industries');
  const simpleLinks = menuItems
    .filter((item) => item.is_active && (item.type === 'url' || item.type === 'URL') && item.name !== 'Industries' && item.name !== 'Products')
    .sort((a, b) => a.position - b.position)
    .map((item) => ({
      label: item.name,
      to: withTrailingSlash(item.url && item.url !== '#' ? `/${item.url}` : `/${nameToSlug(item.name)}`),
    }));

  return {
    products: productsMenu?.items?.length
      ? productsMenu.items.map((cat) => {
        const fallbackCat = NAV_PRODUCTS.find((p) => p.id === cat.category_slug || p.label === cat.name);
        const catSlug = cat.slug || cat.category_slug || nameToSlug(cat.name || '');
        return {
          id: cat.category_slug || String(cat.id),
          label: cat.name,
          url: buildItemUrl(cat),
          items: cat.items?.length
            ? cat.items.map((sub) => {
              const subSlug = sub.slug || sub.category_slug || nameToSlug(sub.name || '');
              return {
                label: sub.name,
                url: catSlug && subSlug ? withTrailingSlash(`/products/${catSlug}/${subSlug}`) : buildItemUrl(sub),
              };
            })
            : (fallbackCat?.items || []),
        };
      })
      : NAV_PRODUCTS,
    industries: industriesMenu?.items?.length
      ? industriesMenu.items.map((item) => {
        const itemSlug = nameToSlug(item.name);
        return {
          label: item.name,
          shortDescription: item.short_description,
          url: withTrailingSlash(`/industries/${itemSlug}`),
          backgroundImage: item.background_image,
          items: item.items?.length
            ? item.items.map((sub) => ({
              label: sub.name,
              url: withTrailingSlash(`/industries/${nameToSlug(sub.name)}`),
            }))
            : [],
        };
      })
      : [],
    simpleLinks: simpleLinks.length ? simpleLinks : NAV_SIMPLE_LINKS,
  };
};

// ─── Static fallback nav links ────────────────────────────────────────────────
const NAV_SIMPLE_LINKS = [
  { label: 'About Us', to: '/about-us/' },
  { label: 'Solutions', to: '/solutions/' },
  { label: 'Partners', to: '/partners/' },
  { label: 'Investors', to: '/investors/' },
  { label: 'Contact Us', to: '/contact-us/' },
];

// ─── Industries Mega Menu ─────────────────────────────────────────────────────

const IndustriesMegaMenu = ({ anchor, onClose, industries, onMenuMouseEnter, onMenuMouseLeave }) => {
  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableScrollLock
      sx={{ pointerEvents: 'none' }}
      slotProps={{ backdrop: { invisible: true, sx: { pointerEvents: 'none' } } }}
      PaperProps={{
        elevation: 0,
        onMouseEnter: onMenuMouseEnter,
        onMouseLeave: onMenuMouseLeave,
        sx: {
          pointerEvents: 'auto',
          width: '100vw !important',
          left: '0 !important',
          right: '0 !important',
          top: '91px !important',
          position: 'fixed !important',
          maxHeight: 'calc(100vh - 111px)',
          maxWidth: '100vw !important',
          borderRadius: 0,
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.05)',
          borderTop: `1px solid ${BORDER_COLOR}`,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          borderLeft: 'none',
          borderRight: 'none',
          mt: '1px',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          bgcolor: 'white',
        },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          ...CONTAINER_SX,
          pt: '48px',
          pb: '48px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              md: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
            gap: {
              md: '24px',
              lg: 'clamp(24px, 2.08vw, 40px)',
              xl: '40px',
            },
            width: '100%',
          }}
        >
          {industries.map((item) => (
            <Box
              key={item.label}
              component={RouterLink}
              to={item.url}
              onClick={onClose}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 0.95,
                  '& img': {
                    transform: 'scale(1.05)',
                  },
                },
              }}
            >
              {/* Image Box Container */}
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '268/192',
                  borderRadius: '15px',
                  overflow: 'hidden',
                  bgcolor: '#f1f5f9',
                }}
              >
                {item.backgroundImage?.path ? (
                  <Box
                    component="img"
                    src={resolveUrl(item.backgroundImage.path)}
                    alt={item.label}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94a3b8',
                      fontSize: '14px',
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    No Image
                  </Box>
                )}
              </Box>

              {/* Title Text */}
              <Typography
                sx={{
                  color: '#178ACB',
                  fontSize: { xs: 14, md: 'clamp(13px, 0.83vw, 16px)', xl: 16 },
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: '600',
                  lineHeight: '22px',
                  mt: 2,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {item.label}
              </Typography>

              {item.shortDescription && (
                <>
                  <Typography
                    sx={{
                      color: '#4B5563',
                      fontSize: { xs: 12, md: 'clamp(11px, 0.73vw, 14px)', xl: 14 },
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: '400',
                      lineHeight: '1.5',
                      mt: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {item.shortDescription}
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 1.5,
                      bgcolor: '#178ACB',
                      color: '#fff',
                      borderRadius: '24px',
                      textTransform: 'uppercase',
                      fontWeight: '700',
                      fontSize: '11px',
                      padding: '6px 20px',
                      alignSelf: 'flex-start',
                      boxShadow: 'none',
                      fontFamily: 'Manrope, sans-serif',
                      pointerEvents: 'none', // Since the parent is already a link
                      '&:hover': {
                        bgcolor: '#126899',
                        boxShadow: 'none',
                      }
                    }}
                  >
                    Explore
                  </Button>
                </>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Popover>
  );
};

// ─── Products Mega Menu ───────────────────────────────────────────────────────

const ProductsMegaMenu = ({ anchor, onClose, products, onMenuMouseEnter, onMenuMouseLeave }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = products[Math.min(activeIdx, products.length - 1)] || { items: [] };

  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableScrollLock
      sx={{ pointerEvents: 'none' }}
      slotProps={{ backdrop: { invisible: true, sx: { pointerEvents: 'none' } } }}
      PaperProps={{
        elevation: 0,
        onMouseEnter: onMenuMouseEnter,
        onMouseLeave: onMenuMouseLeave,
        sx: {
          pointerEvents: 'auto',
          width: '100vw !important',
          left: '0 !important',
          right: '0 !important',
          top: '91px !important',
          position: 'fixed !important',
          maxHeight: 'calc(100vh - 111px)',
          maxWidth: '100vw !important',
          borderRadius: 0,
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.05)',
          borderTop: `1px solid ${BORDER_COLOR}`,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          borderLeft: 'none',
          borderRight: 'none',
          mt: '1px',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          ...CONTAINER_SX,
          display: 'flex',
          alignItems: 'stretch',
          pt: 0,
          pb: 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Left sidebar (Categories) */}
        <Box
          sx={{
            width: 'fit-content',
            flexShrink: 0,
            borderRight: `1px solid ${BORDER_COLOR}`,
            pr: '40px',
            pt: '48px',
            pb: '45px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            gap: '16px',
            boxSizing: 'content-box',
          }}
        >
          {products.map((cat, idx) => (
            <Box
              key={cat.id}
              component={RouterLink}
              to={cat.url}
              onClick={onClose}
              onMouseEnter={() => setActiveIdx(idx)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                py: '5px',
                cursor: 'pointer',
                textDecoration: 'none',
              }}
            >
              <Typography
                sx={{
                  color: '#178ACB',
                  fontSize: { xs: 15, md: 'clamp(14px, 0.94vw, 18px)', lg: 'clamp(15px, 0.94vw, 18px)', xl: 18 },
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: '600',
                  lineHeight: '26px',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {cat.label}
              </Typography>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL || ''}/assets/images/icons/arrow-blue-horizontal.svg`}
                alt=""
                sx={{
                  width: 21,
                  height: 15,
                  objectFit: 'contain',
                  flexShrink: 0,
                  visibility: activeIdx === idx ? 'visible' : 'hidden',
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Right sub-items (Products list) */}
        <Box
          sx={{
            flex: 1,
            pl: { xs: 3, md: 'clamp(60px, 7.15vw, 103px)' },
            pt: '48px',
            pb: '45px',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: active.items.length > 1 ? 'max-content max-content' : 'max-content',
              columnGap: { xs: 4, md: 'clamp(80px, 6.25vw, 120px)' },
              rowGap: '16px',
              pt: '5px', // Aligns the first row of products with the left column's category items (which have py: '5px')
              maxHeight: '100%',
              overflowY: 'auto',
            }}
          >
            {active.items.map((item) => (
              <Typography
                key={item.label}
                component={RouterLink}
                to={item.url}
                onClick={onClose}
                sx={{
                  fontSize: { xs: 13, md: 'clamp(12px, 0.73vw, 14px)', xl: 14 },
                  color: '#000',
                  textDecoration: 'none',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 400,
                  lineHeight: '20px',
                  transition: 'color 0.15s',
                  '&:hover': { color: '#178ACB' },
                }}
              >
                {item.label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Container>
    </Popover>
  );
};

// ─── Badge style — shared for cart and wishlist ───────────────────────────────
const badgeSx = (color) => ({
  '& .MuiBadge-badge': {
    bgcolor: color,
    color: '#fff',
    fontSize: '10px',
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 700,
    minWidth: 16,
    height: 16,
    padding: '0 3px',
    borderRadius: '8px',
    top: 2,
    right: 2,
    border: '1.5px solid #fff',
  },
});

// ─── Mobile drawer style helpers ─────────────────────────────────────────────

const drawerNavItemSx = (isActive) => ({
  borderRadius: 0,
  py: 1.25,
  borderBottom: `1px solid ${BORDER_COLOR}`,
  color: isActive ? BRAND_BLUE : '#333',
});

const drawerChildItemSx = {
  pl: 3,
  py: 0.75,
  gap: '10px',
};

const drawerChildBarSx = {
  width: '1px',
  minWidth: '1px',
  height: '12px',
  alignSelf: 'center',
  bgcolor: BRAND_BLUE,
};

const drawerChildTextTypographyProps = {
  fontSize: '0.85rem',
  fontFamily: 'Manrope, sans-serif',
  fontWeight: 400,
  color: '#555',
  lineHeight: '20px',
};

const drawerAuthItemSx = {
  borderRadius: '8px',
  mx: 0.5,
  color: '#374151',
  transition: 'background-color 0.15s, color 0.15s',
  '&:hover': { bgcolor: '#f0f7ff', color: BRAND_BLUE },
};

// ─── Header ──────────────────────────────────────────────────────────────────

// ─── Popup triggers ────────────────────────────────────────────────────────────
// Each trigger owns its own open/close state (and its popup). Previously that
// state lived in <Header>, so tapping Search/Cart/Menu re-rendered the ENTIRE
// header (nav + mega-menu) before the popup could appear — measured as the
// slowest interactions on the site (INP). Now only the tiny component re-renders.
// CartDrawer/SearchModal are lazy chunks: out of the main bundle, and they mount
// closed (MUI renders nothing until open), so the open animation still plays.

const SearchButton = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the search overlay on navigation — clicking any link/page while it's
  // open (e.g. a suggestion result) shouldn't leave it open over the new page.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <IconButton aria-label="Search" onClick={() => setOpen(true)} sx={iconBtnSx}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/assets/images/header/Search_1_.svg`}
          alt="Search"
          sx={{ width: 'clamp(22px, 1.46vw, 28px)', height: 'clamp(22px, 1.46vw, 28px)' }}
        />
      </IconButton>
      <Suspense fallback={null}>
        <SearchModal open={open} onClose={() => setOpen(false)} />
      </Suspense>
    </>
  );
};

const CartButton = ({ cartCount }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <IconButton aria-label="Cart" onClick={() => setOpen(true)} sx={iconBtnSx}>
        <Badge badgeContent={cartCount} sx={badgeSx('#005DAA')}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/assets/images/header/Shopping_Cart_1_.svg`}
            alt="Cart"
            sx={{ width: 'clamp(22px, 1.46vw, 28px)', height: 'clamp(22px, 1.46vw, 28px)' }}
          />
        </Badge>
      </IconButton>
      <Suspense fallback={null}>
        <CartDrawer open={open} onClose={() => setOpen(false)} />
      </Suspense>
    </>
  );
};

const MobileNavDrawer = ({
  navSimpleLinks, navIndustries, navProducts,
  isActiveRoute, isIndustriesActive, isProductsActive,
  isAuthenticated, logout,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <>
      {/* Mobile hamburger */}
      <IconButton
        sx={{ display: { xs: 'inline-flex', lg: 'none' } }}
        onClick={() => setDrawerOpen(true)}
        aria-label="Open menu"
      >
        <MenuIcon />
      </IconButton>

      {/* ── Mobile Drawer ── */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: 300, height: '100%', display: 'flex', flexDirection: 'column' }} role="presentation">

          {/* Drawer header — logo + close */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            borderBottom: `1px solid ${BORDER_COLOR}`,
            flexShrink: 0,
          }}>
            <Box
              component={RouterLink}
              to="/"
              onClick={closeDrawer}
              sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo/header-logo.svg`}
                alt="Dynalog India"
                sx={{ height: 30, width: 'auto', objectFit: 'contain' }}
              />
            </Box>
            <IconButton
              onClick={closeDrawer}
              size="small"
              sx={{ color: '#555', '&:hover': { bgcolor: '#f0f7ff', color: BRAND_BLUE } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Nav items — scrollable */}
          <List component="nav" aria-label="Primary" dense disablePadding sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 0.5 }}>
            {navSimpleLinks.slice(0, 1).map((item) => (
              <ListItemButton
                key={item.label}
                component={RouterLink}
                to={item.to}
                onClick={closeDrawer}
                sx={drawerNavItemSx(isActiveRoute(item.to))}
              >
                <ListItemText primary={item.label} primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '0.9375rem' }} />
              </ListItemButton>
            ))}

            <ListItemButton
              onClick={() => setMobileIndustriesOpen((p) => !p)}
              sx={drawerNavItemSx(isIndustriesActive || mobileIndustriesOpen)}
            >
              <ListItemText primary="Industries" primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '0.9375rem' }} />
              {mobileIndustriesOpen
                ? <ExpandLess sx={{ color: BRAND_BLUE, fontSize: 20 }} />
                : <ExpandMore sx={{ color: '#9ca3af', fontSize: 20 }} />}
            </ListItemButton>
            <Collapse in={mobileIndustriesOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding dense>
                {navIndustries.map((item) => (
                  <ListItemButton key={item.label} sx={drawerChildItemSx} component={RouterLink} to={item.url} onClick={closeDrawer}>
                    <Box aria-hidden="true" sx={drawerChildBarSx} />
                    <ListItemText primary={item.label} primaryTypographyProps={drawerChildTextTypographyProps} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>

            <ListItemButton
              onClick={() => setMobileProductsOpen((p) => !p)}
              sx={drawerNavItemSx(isProductsActive || mobileProductsOpen)}
            >
              <ListItemText primary="Products" primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '0.9375rem' }} />
              {mobileProductsOpen
                ? <ExpandLess sx={{ color: BRAND_BLUE, fontSize: 20 }} />
                : <ExpandMore sx={{ color: '#9ca3af', fontSize: 20 }} />}
            </ListItemButton>
            <Collapse in={mobileProductsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding dense>
                {navProducts.map((cat) => (
                  <ListItemButton key={cat.id} sx={drawerChildItemSx} component={RouterLink} to={cat.url} onClick={closeDrawer}>
                    <Box aria-hidden="true" sx={drawerChildBarSx} />
                    <ListItemText primary={cat.label} primaryTypographyProps={drawerChildTextTypographyProps} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>

            {navSimpleLinks.slice(1).map((item) => (
              <ListItemButton
                key={item.label}
                component={RouterLink}
                to={item.to}
                onClick={closeDrawer}
                sx={drawerNavItemSx(isActiveRoute(item.to))}
              >
                <ListItemText primary={item.label} primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: '0.9375rem' }} />
              </ListItemButton>
            ))}
          </List>

          {/* Auth section — pinned to bottom */}
          <Box sx={{ borderTop: `1px solid ${BORDER_COLOR}`, px: 1, pt: 0.5, pb: 1.5, flexShrink: 0 }}>
            {isAuthenticated ? (
              <List dense disablePadding>
                <ListItemButton component={RouterLink} to="/profile/edit/" onClick={closeDrawer} sx={drawerAuthItemSx}>
                  <Box component="img" src={`${process.env.PUBLIC_URL}/assets/images/icons/profile/my-profile.svg`} alt="" sx={{ width: 18, height: 18, mr: 1.5, flexShrink: 0 }} />
                  <ListItemText primary="My Profile" primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem' }} />
                </ListItemButton>
                <ListItemButton component={RouterLink} to="/profile/orders/" onClick={closeDrawer} sx={drawerAuthItemSx}>
                  <Box component="img" src={`${process.env.PUBLIC_URL}/assets/images/icons/profile/order.svg`} alt="" sx={{ width: 18, height: 18, mr: 1.5, flexShrink: 0 }} />
                  <ListItemText primary="My Orders" primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem' }} />
                </ListItemButton>
                {WISHLIST_ENABLED && (
                  <ListItemButton component={RouterLink} to="/wishlist/" onClick={closeDrawer} sx={drawerAuthItemSx}>
                    <FavoriteBorder sx={{ fontSize: 18, color: '#9ca3af', mr: 1.5, flexShrink: 0 }} />
                    <ListItemText primary="My Wishlist" primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem' }} />
                  </ListItemButton>
                )}
                <ListItemButton
                  onClick={() => { logout(); closeDrawer(); }}
                  sx={{ ...drawerAuthItemSx, color: '#ef4444', '&:hover': { bgcolor: '#fff5f5', color: '#ef4444' } }}
                >
                  <LogoutOutlined sx={{ fontSize: 18, color: '#ef4444', mr: 1.5, flexShrink: 0 }} />
                  <ListItemText primary="Logout" primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem', color: '#ef4444' }} />
                </ListItemButton>
              </List>
            ) : (
              <List dense disablePadding>
                <ListItemButton component={RouterLink} to="/login/" onClick={closeDrawer} sx={drawerAuthItemSx}>
                  <PersonOutline sx={{ fontSize: 18, color: '#9ca3af', mr: 1.5, flexShrink: 0 }} />
                  <ListItemText primary="Login" primaryTypographyProps={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.875rem' }} />
                </ListItemButton>
              </List>
            )}
          </Box>

        </Box>
      </Drawer>
    </>
  );
};

const Header = () => {
  const location = useLocation();
  const cartCount = useSelector((state) => state.cart?.quantity || 0);
  const { user, isAuthenticated, logout } = useAuth();

  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const closeProfileMenu = () => setProfileMenuAnchor(null);

  const displayName = user
    ? (user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.name || '')
    : '';
  const initials = displayName
    ? displayName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : 'U';

  const [navProducts, setNavProducts] = useState(NAV_PRODUCTS);
  const [navIndustries, setNavIndustries] = useState([]);
  const [navSimpleLinks, setNavSimpleLinks] = useState(NAV_SIMPLE_LINKS);

  useEffect(() => {
    getMenuItems().then((items) => {
      if (items.length) {
        const { products, industries, simpleLinks } = transformMenuData(items);
        setNavProducts(products);
        setNavIndustries(industries);
        setNavSimpleLinks(simpleLinks);
      }
    });
  }, []);

  const [megaAnchor, setMegaAnchor] = useState(null);
  const [industriesAnchor, setIndustriesAnchor] = useState(null);


  const megaHoverTimer = useRef(null);
  const industriesHoverTimer = useRef(null);

  const openMegaMenu = (e) => { clearTimeout(megaHoverTimer.current); setMegaAnchor(e.currentTarget); };
  const closeMegaMenuDelayed = () => { megaHoverTimer.current = setTimeout(() => setMegaAnchor(null), 300); };

  const openIndustriesMenu = (e) => { clearTimeout(industriesHoverTimer.current); setIndustriesAnchor(e.currentTarget); };
  const closeIndustriesMenuDelayed = () => { industriesHoverTimer.current = setTimeout(() => setIndustriesAnchor(null), 300); };


  // Active-route helpers
  const isActiveRoute = (to) => {
    const cleanPath = location.pathname.replace(/\/$/, '');
    const cleanTo = to.replace(/\/$/, '');
    return cleanPath === cleanTo;
  };

  const isProductsActive = location.pathname.startsWith('/products') ||
    location.pathname.startsWith('/categories') ||
    location.pathname.startsWith('/category');

  const isIndustriesActive = location.pathname.startsWith('/industries');

  return (
    <>
      <style>{`
        .header-nav-container {
          gap: clamp(12px, 1.8vw, 40px);
        }
      `}</style>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ top: 0, bgcolor: '#fff', borderBottom: `1px solid ${BORDER_COLOR}`, zIndex: 1200 }}
      >
        <Container
          maxWidth={false}
          sx={CONTAINER_SX}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight: '90px !important',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* ── Logo ── */}
            <Box
              component={RouterLink}
              to="/"
              sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
            >
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL || ''}/assets/images/logo/header-logo.svg`}
                alt="Dynalog India"
                sx={{
                  height: 51, /* { xs: 36, md: 'clamp(38px, 2.66vw, 51px)' } */
                  width: 'auto',
                  objectFit: 'contain',
                  '@media (max-width:425px)': { height: 40 },
                }}
              />
            </Box>

            {/* ── Desktop Nav ── */}
            <Box
              component="nav"
              aria-label="Primary"
              className="header-nav-container"
              sx={{
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'center',
                // With login/cart/Shop Now hidden, the right cluster shrinks to just
                // the search icon — centering the links then leaves a large empty gap
                // between them and that lone icon. Shifting to flex-end keeps the
                // links flowing naturally up against whatever's actually on the right.
                justifyContent: HEADER_ACTIONS_HIDDEN ? 'flex-end' : 'center',
                flex: 1,
                pr: HEADER_ACTIONS_HIDDEN ? 'clamp(20px, 2.5vw, 48px)' : 0,
              }}
            >
              {/* About Us (first simple link) */}
              {navSimpleLinks.slice(0, 1).map((item) => (
                <Typography
                  key={item.label}
                  component={RouterLink}
                  to={item.to}
                  sx={navLinkSx(isActiveRoute(item.to))}
                >
                  {item.label}
                </Typography>
              ))}

              {/* Industries dropdown */}
              <Button
                component={RouterLink}
                to="/industries/"
                onMouseEnter={openIndustriesMenu}
                onMouseLeave={closeIndustriesMenuDelayed}
                endIcon={
                  <KeyboardArrowDown
                    sx={{
                      fontSize: 16,
                      ml: '-6px',
                      transition: 'transform 0.2s',
                      transform: Boolean(industriesAnchor) ? 'rotate(180deg)' : 'none',
                    }}
                  />
                }
                sx={navBtnSx(Boolean(industriesAnchor) || isIndustriesActive)}
              >
                Industries
              </Button>
              <IndustriesMegaMenu
                anchor={industriesAnchor}
                onClose={() => setIndustriesAnchor(null)}
                industries={navIndustries}
                onMenuMouseEnter={() => clearTimeout(industriesHoverTimer.current)}
                onMenuMouseLeave={closeIndustriesMenuDelayed}
              />

              {/* Products mega menu */}
              <Button
                onMouseEnter={openMegaMenu}
                onMouseLeave={closeMegaMenuDelayed}
                endIcon={
                  <KeyboardArrowDown
                    sx={{
                      fontSize: 16,
                      ml: '-6px',
                      transition: 'transform 0.2s',
                      transform: megaAnchor ? 'rotate(180deg)' : 'none',
                    }}
                  />
                }
                sx={navBtnSx(Boolean(megaAnchor) || isProductsActive)}
              >
                Products
              </Button>
              <ProductsMegaMenu
                anchor={megaAnchor}
                onClose={() => setMegaAnchor(null)}
                products={navProducts}
                onMenuMouseEnter={() => clearTimeout(megaHoverTimer.current)}
                onMenuMouseLeave={closeMegaMenuDelayed}
              />

              {/* Remaining simple links */}
              {navSimpleLinks.slice(1).map((item) => (
                <Typography
                  key={item.label}
                  component={RouterLink}
                  to={item.to}
                  sx={navLinkSx(isActiveRoute(item.to))}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>

            {/* ── Profile dropdown — Portal, placed before icon row for clean JSX ── */}
            <Menu
              anchorEl={profileMenuAnchor}
              open={Boolean(profileMenuAnchor)}
              onClose={closeProfileMenu}
              disableScrollLock
              PaperProps={{
                elevation: 6,
                sx: {
                  mt: 1,
                  minWidth: 220,
                  borderRadius: '10px',
                  border: `1px solid ${BORDER_COLOR}`,
                  overflow: 'hidden',
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* User info header */}
              <Box sx={{ px: 2, py: 1.75, bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER_COLOR}` }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#1a1a2e', fontFamily: 'Manrope, sans-serif', lineHeight: 1.3 }}>
                  {displayName || 'My Account'}
                </Typography>
                {user?.email && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.4, fontFamily: 'Manrope, sans-serif' }}>
                    {user.email}
                  </Typography>
                )}
              </Box>

              <MenuItem
                component={RouterLink}
                to="/profile/edit/"
                onClick={closeProfileMenu}
                sx={dropdownItemSx}
              >
                <Box component="img" src={`${process.env.PUBLIC_URL}/assets/images/icons/profile/my-profile.svg`} alt="" sx={{ width: 18, height: 18, flexShrink: 0 }} />
                My Profile
              </MenuItem>

              <MenuItem
                component={RouterLink}
                to="/profile/orders/"
                onClick={closeProfileMenu}
                sx={dropdownItemSx}
              >
                <Box component="img" src={`${process.env.PUBLIC_URL}/assets/images/icons/profile/order.svg`} alt="" sx={{ width: 18, height: 18, flexShrink: 0 }} />
                My Orders
              </MenuItem>

              {WISHLIST_ENABLED && (
                <MenuItem
                  component={RouterLink}
                  to="/wishlist/"
                  onClick={closeProfileMenu}
                  sx={dropdownItemSx}
                >
                  <FavoriteBorder sx={dropdownIconSx} />
                  My Wishlist
                </MenuItem>
              )}

              <Divider sx={{ my: 0.5 }} />

              <MenuItem
                onClick={() => { logout(); closeProfileMenu(); }}
                sx={{ ...dropdownItemSx, color: '#ef4444', '&:hover': { bgcolor: '#fff5f5', color: '#ef4444' } }}
              >
                <LogoutOutlined sx={{ fontSize: 18, color: '#ef4444' }} />
                Logout
              </MenuItem>
            </Menu>

            {/* ── Right icons & CTA ── */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(10px, 0.83vw, 16px)',
                ml: { xs: 'auto', lg: 0 },
                flexShrink: 0,
              }}
            >
              {/* Icon group — tight together */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'clamp(8px, 0.73vw, 14px)',
                  '@media (max-width:425px)': { ml: '15px' },
                }}
              >
                {/* Search */}
                <SearchButton />

                {/* Profile avatar (authenticated) or login link (guest) */}
                {!HEADER_ACTIONS_HIDDEN && (isAuthenticated ? (
                  <IconButton
                    aria-label="Account"
                    onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
                    sx={{ p: 0.25 }}
                  >
                    <Avatar
                      sx={{
                        width: 'clamp(26px, 1.56vw, 30px)',
                        height: 'clamp(26px, 1.56vw, 30px)',
                        bgcolor: BRAND_BLUE,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        fontFamily: 'Manrope, sans-serif',
                        transition: 'opacity 0.15s',
                        '&:hover': { opacity: 0.85 },
                      }}
                    >
                      {initials}
                    </Avatar>
                  </IconButton>
                ) : (
                  <IconButton
                    aria-label="Login"
                    component={RouterLink}
                    to="/login/"
                    sx={iconBtnSx}
                  >
                    <Box
                      component="img"
                      src={`${process.env.PUBLIC_URL}/assets/images/header/user 1.svg`}
                      alt="Login"
                      sx={{ width: 'clamp(22px, 1.46vw, 28px)', height: 'clamp(22px, 1.46vw, 28px)' }}
                    />
                  </IconButton>
                ))}

                {/* Cart — opens side drawer */}
                {!HEADER_ACTIONS_HIDDEN && (
                  <CartButton cartCount={cartCount} />
                )}
              </Box>

              {/* SHOP NOW — pill shape matching Figma design */}
              {!HEADER_ACTIONS_HIDDEN && (
                <Button
                  component={RouterLink}
                  to="/shop/"
                  variant="outlined"
                  sx={{
                    height: 'clamp(36px, 2.24vw, 43px)',
                    px: 'clamp(14px, 1.25vw, 24px)',
                    borderRadius: '20px',
                    border: `1.5px solid ${BRAND_BLUE}`,
                    color: BRAND_BLUE,
                    textTransform: 'none',
                    fontSize: 'clamp(12px, 0.75vw, 14px)',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                    whiteSpace: 'nowrap',
                    display: { xs: 'none', sm: 'inline-flex' },
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: BRAND_BLUE,
                      color: '#fff',
                      borderColor: BRAND_BLUE,
                    },
                  }}
                >
                  SHOP NOW
                </Button>
              )}

              {/* Mobile hamburger */}
              <MobileNavDrawer
                navSimpleLinks={navSimpleLinks}
                navIndustries={navIndustries}
                navProducts={navProducts}
                isActiveRoute={isActiveRoute}
                isIndustriesActive={isIndustriesActive}
                isProductsActive={isProductsActive}
                isAuthenticated={isAuthenticated}
                logout={logout}
              />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>


    </>
  );
};

// ─── Shared style objects ─────────────────────────────────────────────────────

// Nav link — static Typography links
const navLinkSx = (isActive) => ({
  color: isActive ? BRAND_BLUE : NAV_COLOR,
  textDecoration: 'none',
  fontSize: 'clamp(14px, 0.85vw, 16px)',
  fontFamily: 'Manrope, sans-serif',
  fontWeight: isActive ? 600 : 400,
  lineHeight: '26px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  borderBottom: isActive ? `2px solid ${BRAND_BLUE}` : '2px solid transparent',
  paddingBottom: '2px',
  transition: 'color 0.15s, border-color 0.15s',
  '&:hover': {
    color: BRAND_BLUE,
    borderBottom: `2px solid ${BRAND_BLUE}`,
  },
});

// Nav button — dropdown triggers (Industries, Products)
const navBtnSx = (isActive) => ({
  color: isActive ? BRAND_BLUE : NAV_COLOR,
  fontSize: 'clamp(14px, 0.85vw, 16px)',
  fontFamily: 'Manrope, sans-serif',
  fontWeight: isActive ? 600 : 400,
  lineHeight: '26px',
  textTransform: 'none',
  px: 0,
  minWidth: 0,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  borderBottom: isActive ? `2px solid ${BRAND_BLUE}` : '2px solid transparent',
  paddingTop: 0,
  paddingBottom: '2px',
  borderRadius: 0,
  transition: 'color 0.15s, border-color 0.15s',
  '&:hover': {
    color: BRAND_BLUE,
    bgcolor: 'transparent',
    borderBottom: `2px solid ${BRAND_BLUE}`,
  },
});

// Icon button — Search, Cart, Login
const iconBtnSx = {
  p: 0,
  color: '#374151',
  transition: 'opacity 0.15s',
  '&:hover': { opacity: 0.7, bgcolor: 'transparent' },
};

// Profile dropdown menu items
const dropdownItemSx = {
  py: 1.2,
  gap: 1.5,
  fontSize: '0.875rem',
  fontFamily: 'Manrope, sans-serif',
  color: '#374151',
  '&:hover': { color: BRAND_BLUE, bgcolor: '#f0f7ff' },
};

const dropdownIconSx = { fontSize: 18, color: '#9ca3af' };

export default Header;
